// === WOONHUIS — form validation ===
// Pure validation extracted verbatim from WoonhuisScreen. validateWoonhuis returns a map of
// {key, params} i18n entries (the screen translates them); validateBuildingNumberField covers the
// per-field onBlur rules for the building number inputs; scrollToFirstError focuses the first
// errored field. `now` and the lock flags are injected so the rules are deterministic in tests.
// Mirrors inboedel/validation.ts. NOTE: several screens.woonhuis.validation.* keys are also
// borrowed by Inboedel — do not rename them.

export interface WoonhuisValidationEntry {
  key: string;
  params?: Record<string, unknown>;
}

export interface ValidateWoonhuisOptions {
  now: Date;
  isAddressLocked: boolean;
  isBuildingDetailsLocked: boolean;
}

export function validateWoonhuis(data: any, opts: ValidateWoonhuisOptions): Record<string, WoonhuisValidationEntry> {
  const { isAddressLocked, isBuildingDetailsLocked } = opts;
  const errors: Record<string, WoonhuisValidationEntry> = {};
  const ba = data?.Contractdocument?.PP?.BA;

  // Validate address fields (only if NOT locked)
  if (!isAddressLocked) {
    if (!ba?.BA_PCODE) {
      errors['Contractdocument.PP.BA.BA_PCODE'] = { key: 'screens.woonhuis.validation.postalCodeRequired' };
    } else if (!/^[0-9]{4}\s?[A-Z]{2}$/i.test(ba.BA_PCODE)) {
      errors['Contractdocument.PP.BA.BA_PCODE'] = { key: 'screens.woonhuis.validation.postalCodeInvalid' };
    }
    if (!ba?.BA_HUISNR || String(ba.BA_HUISNR).trim() === '') {
      errors['Contractdocument.PP.BA.BA_HUISNR'] = { key: 'screens.woonhuis.validation.houseNumberRequired' };
    }
    // Address validation - show visible error if street/city not populated
    if (!ba?.BA_STRAAT || !ba?.BA_PLAATS) {
      errors['Contractdocument.PP.BA.BA_STRAAT'] = { key: 'screens.woonhuis.validation.addressNotValidated' };
    }
  }

  // Validate building details (only if NOT locked)
  if (!isBuildingDetailsLocked) {
    if (!ba?.BA_BOUWJR) {
      errors['Contractdocument.PP.BA.BA_BOUWJR'] = { key: 'screens.woonhuis.validation.constructionYearRequired' };
    } else {
      const year = Number(ba.BA_BOUWJR);
      const currentYear = opts.now.getFullYear();
      if (year < 1800 || year > currentYear) {
        errors['Contractdocument.PP.BA.BA_BOUWJR'] = { key: 'screens.woonhuis.validation.constructionYearInvalid', params: { maxYear: currentYear } };
      }
    }
    if (!ba?.BA_MONUMEN) errors['Contractdocument.PP.BA.BA_MONUMEN'] = { key: 'screens.woonhuis.validation.monumentRequired' };
    if (!ba?.BA_TYPEWON) errors['Contractdocument.PP.BA.BA_TYPEWON'] = { key: 'screens.woonhuis.validation.houseTypeRequired' };
    if (!ba?.BA_AARDBW) errors['Contractdocument.PP.BA.BA_AARDBW'] = { key: 'screens.woonhuis.validation.wallTypeRequired' };
    if (!ba?.BA_AARDDAK) errors['Contractdocument.PP.BA.BA_AARDDAK'] = { key: 'screens.woonhuis.validation.roofTypeRequired' };
    if (!ba?.BA_BDRFBEL) errors['Contractdocument.PP.BA.BA_BDRFBEL'] = { key: 'screens.woonhuis.validation.adjacentBuildingRequired' };
    if (!ba?.BA_WONING) errors['Contractdocument.PP.BA.BA_WONING'] = { key: 'screens.woonhuis.validation.propertyUseRequired' };
    if (!ba?.BA_HERBVST) errors['Contractdocument.PP.BA.BA_HERBVST'] = { key: 'screens.woonhuis.validation.rebuildValueRequired' };
    if (!ba?.BA_EIGENWO) errors['Contractdocument.PP.BA.BA_EIGENWO'] = { key: 'screens.woonhuis.validation.ownHomeRequired' };
    if (!ba?.BA_PERMBEW) errors['Contractdocument.PP.BA.BA_PERMBEW'] = { key: 'screens.woonhuis.validation.permanentResidentRequired' };

    // Ground floor area - optional but must be a valid number if filled
    if (ba?.BA_BGM2 !== undefined && ba?.BA_BGM2 !== null && ba?.BA_BGM2 !== '') {
      const bgm2Value = Number(ba.BA_BGM2);
      if (isNaN(bgm2Value)) {
        errors['Contractdocument.PP.BA.BA_BGM2'] = { key: 'screens.woonhuis.validation.groundFloorAreaInvalid' };
      }
    }
  }

  // Always validate these fields
  const op = data?.Contractdocument?.PP?.OP;

  // Woonhuis is first in workflow - always validate OP_GSS here
  if (!op?.OP_GSS) {
    errors['Contractdocument.PP.OP.OP_GSS'] = { key: 'screens.woonhuis.validation.familyCompositionRequired' };
  }

  // Insured sum (Herbouwwaarde) validation
  if (!op?.OP_VERZSOM) {
    errors['Contractdocument.PP.OP.OP_VERZSOM'] = { key: 'screens.woonhuis.validation.insuredSumRequired' };
  }

  // Deductible amount validation
  if (op?.OP_ERB === undefined || op?.OP_ERB === null || op?.OP_ERB === '') {
    errors['Contractdocument.PP.OP.OP_ERB'] = { key: 'screens.woonhuis.validation.deductibleRequired' };
  }

  // Vaste taxatie: end date + VRT answer are required while 'V' is selected
  if (ba?.BA_HERBVST === 'V') {
    if (!ba?.BA_EINDGTX) {
      errors['Contractdocument.PP.BA.BA_EINDGTX'] = { key: 'screens.woonhuis.validation.valuationEndDateRequired' };
    }
    if (!data?.Supportsection?.Divers?.TaxatieVRT) {
      errors['Supportsection.Divers.TaxatieVRT'] = { key: 'screens.woonhuis.validation.vrtValuationRequired' };
    }
  }

  // Storm deductible waiver validation
  if (!op?.OP_STORM) {
    errors['Contractdocument.PP.OP.OP_STORM'] = { key: 'screens.woonhuis.validation.stormDeductibleWaiverRequired' };
  }

  return errors;
}

/**
 * Numeric range validation for the building number fields (forwarded from WoningSpecificaties
 * onBlur). Returns an entry to SET, null to CLEAR the field's error, or undefined for a
 * fieldPath this rule set does not cover (no state change) — mirroring the original
 * handleBuildingNumberBlur behavior exactly.
 */
export function validateBuildingNumberField(
  fieldPath: string,
  raw: string,
  opts: { now: Date },
): WoonhuisValidationEntry | null | undefined {
  const value = raw ? parseInt(raw, 10) : null;
  if (fieldPath === 'Contractdocument.PP.BA.BA_BOUWJR') {
    if (!value) { return { key: 'screens.woonhuis.validation.constructionYearRequired' }; }
    const currentYear = opts.now.getFullYear();
    if (value < 1800 || value > currentYear) {
      return { key: 'screens.woonhuis.validation.constructionYearInvalid', params: { maxYear: currentYear } };
    } else return null;
  } else if (fieldPath === 'Contractdocument.PP.BA.BA_BGM2') {
    if (raw && isNaN(Number(raw))) return { key: 'screens.woonhuis.validation.groundFloorAreaInvalid' }; else return null;
  } else if (fieldPath === 'Contractdocument.PP.BA.BA_TOPPBGB') {
    if (value !== null && value > 100) return { key: 'screens.woonhuis.validation.outbuildingsMax' }; else return null;
  }
  return undefined;
}

// Mapping of validation error keys to form element IDs
export const WOONHUIS_ERROR_FIELD_IDS: Record<string, string> = {
  // Address validation now uses 'Contractdocument.PP.BA.BA_STRAAT' key which maps to 'straat' below
  'Contractdocument.PP.BA.BA_PCODE': 'postcode',
  'Contractdocument.PP.BA.BA_HUISNR': 'huisnr',
  'Contractdocument.PP.BA.BA_TOEVOEG': 'toevoeg',
  'Contractdocument.PP.BA.BA_STRAAT': 'straat',
  'Contractdocument.PP.BA.BA_PLAATS': 'plaats',
  'Contractdocument.PP.BA.BA_BOUWJR': 'bouwjaar',
  'Contractdocument.PP.BA.BA_TYPEWON': 'typewon',
  'Contractdocument.PP.BA.BA_AARDBW': 'aardbw',
  'Contractdocument.PP.BA.BA_AARDDAK': 'aarddak',
  'Contractdocument.PP.BA.BA_WONING': 'woning',
  'Contractdocument.PP.BA.BA_HERBVST': 'herbvst',
  'Contractdocument.PP.BA.BA_EINDGTX': 'eindgtx',
  'Supportsection.Divers.TaxatieVRT': 'taxvrt',
  'Contractdocument.PP.BA.BA_MONUMEN': 'monumen',
  'Contractdocument.PP.BA.BA_BDRFBEL': 'bdrfbel',
  'Contractdocument.PP.BA.BA_EIGENWO': 'eigenwo',
  'Contractdocument.PP.BA.BA_PERMBEW': 'permbew',
  'Contractdocument.PP.BA.BA_BGM2': 'bgm2',
  'Contractdocument.PP.OP.OP_GSS': 'gezin',
  'Contractdocument.PP.OP.OP_VERZSOM': 'verzekerd-bedrag',
  'Contractdocument.PP.OP.OP_ERB': 'erb',
};

// Scroll to the first field with a validation error
export function scrollToFirstError(errors: Record<string, string>): void {
  const errorKeys = Object.keys(errors);
  if (errorKeys.length === 0) return;

  // Find the first error field that has a corresponding element ID
  for (const errorKey of errorKeys) {
    const elementId = WOONHUIS_ERROR_FIELD_IDS[errorKey];
    if (elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
        return;
      }
    }
  }

  // Fallback to scrolling to top if no element found
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
